import { useState } from "react";
import { useNavigate, useMatch } from "react-router-dom";
import {
  useBreakpoints,
  LoadingShim,
  LoadingSkeleton,
} from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import { catcat as cc } from "cerulean";
import { entities } from "byzantine";
import { useDeleteBulkPayment } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import { useCancelDualApproval } from "byzantine/src/dbbl/businessLogic/entities/dualApprovals";
import {
  useFetchPaginatedFileImports,
  useFormattedPaginatedFileImports,
  useFileImportsTotalCount,
} from "byzantine/src/dbbl/businessLogic/entities/fileImportActivity";
import type { FormattedFileImportItem } from "byzantine/src/dbbl/businessLogic/entities/fileImportActivity/types";
import PageNavigation from "../../page_navigation/PageNavigation";
import { SudoProvider } from "../../SudoContext";
import { EmptyState, SearchBar } from "../common";
import FileImportTable from "./FileImportTable";
import CancelFileImportDialog from "./CancelFileImportDialog";
import usePaymentActivityContainer from "../hooks";
import { FilterChips } from "../Filters/filterConfig";
import FilterContainer from "../Filters/FilterContainer";
import {
  PAYMENT_ACTIVITY_ROUTE,
  getFileImportReverseRoute,
  getFileImportReverseFromDrawerRoute,
} from "../routes";
import BulkReverseFlow from "../Bulk/BulkReverseFlow";

const FileImportContainer = () => {
  const { m, l } = useBreakpoints();
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const [cancelTargetId, setCancelTargetId] = useState<
    | API.BulkPaymentId
    | API.WireImport.WireImportBatchId
    | API.DualApprovalId
    | null
  >(null);
  const [cancelTargetType, setCancelTargetType] = useState<
    "ach" | "wire" | null
  >(null);
  const { deleteBulkPayment } = useDeleteBulkPayment();
  const { cancelDualApproval } = useCancelDualApproval();

  const reverseFromTableMatch = useMatch(
    `${getFileImportReverseRoute(":reverseFileImportId")}/*`
  );
  const reverseFromDrawerMatch = useMatch(
    `${getFileImportReverseFromDrawerRoute(":fileImportId")}/*`
  );
  const reverseFileImportId =
    reverseFromTableMatch?.params.reverseFileImportId ??
    reverseFromDrawerMatch?.params.fileImportId ??
    null;
  const reverseFromDrawer = Boolean(reverseFromDrawerMatch);

  const {
    state,
    formattedPaginatedPayments,
    paymentsLoading,
    bootstrapLoading,
    pagination,
    handleSearchChange,
    handleReset,
    handleSetFilters,
    refetchCurrentPage,
  } = usePaymentActivityContainer({
    useFetchPaginated: useFetchPaginatedFileImports,
    useFormattedPaginated: useFormattedPaginatedFileImports,
    useTotal: useFileImportsTotalCount,
    useBootstrap: entities.fileImportPayments.bootstrap.useBootstrap,
  });

  const toggleCancelDialog = (
    uuid?:
      | API.BulkPaymentId
      | API.WireImport.WireImportBatchId
      | API.DualApprovalId,
    type?: "ach" | "wire"
  ) => {
    setCancelTargetId(uuid ?? null);
    setCancelTargetType(type ?? null);
  };

  const handleCancelPayment = () => {
    if (!cancelTargetId) return;

    if (cancelTargetType === "ach") {
      deleteBulkPayment(cancelTargetId as API.BulkPaymentId, {
        onSuccess: () => {
          refetchCurrentPage();
          toggleCancelDialog();
        },
      });
    } else if (cancelTargetType === "wire") {
      cancelDualApproval(
        cancelTargetId as API.DualApprovalId,
        "WireImportBatch",
        {
          onSuccess: () => {
            refetchCurrentPage();
            toggleCancelDialog();
          },
        }
      );
    }
  };

  const renderTable = () => {
    if (paymentsLoading) return <LoadingShim isLoading />;
    if (
      (formattedPaginatedPayments as FormattedFileImportItem[]).length === 0
    ) {
      return <EmptyState state={state} handleReset={handleReset} />;
    }

    return (
      <div className={cc({ "margin--top--l": !(m || l) })}>
        <FileImportTable
          payments={formattedPaginatedPayments as FormattedFileImportItem[]}
          handleToggleCancelDialog={toggleCancelDialog}
        />
        <PageNavigation
          pagination={pagination}
          marginClasses="margin--all--l"
        />
      </div>
    );
  };

  return (
    <LoadingSkeleton isLoading={bootstrapLoading}>
      <div className="margin--top--xl">
        <SearchBar
          handleSearchChange={handleSearchChange}
          searchValue={state.searchValue}
          placeholder={l10n.getString(
            "payment-activity-search-file-import-label"
          )}
        />
      </div>
      <div>
        <FilterContainer
          setFilters={handleSetFilters}
          filters={state.filters}
          allowedFilters={[
            FilterChips.FILE_IMPORT_METHOD,
            FilterChips.DATE,
            FilterChips.BULK_STATUS,
            FilterChips.AMOUNT,
            FilterChips.ACCOUNT,
          ]}
          parentSelectableFilters={[
            FilterChips.BULK_STATUS,
            FilterChips.AMOUNT,
            FilterChips.ACCOUNT,
          ]}
        />
      </div>
      {renderTable()}
      {cancelTargetId && (
        <CancelFileImportDialog
          isOpen={Boolean(cancelTargetId)}
          handleClose={() => toggleCancelDialog()}
          handleCancel={handleCancelPayment}
        />
      )}
      {reverseFileImportId && (
        <SudoProvider
          basename={
            reverseFromDrawer
              ? getFileImportReverseFromDrawerRoute(reverseFileImportId)
              : getFileImportReverseRoute(reverseFileImportId)
          }
        >
          <BulkReverseFlow
            bulkPaymentId={reverseFileImportId}
            reverseRoute={
              reverseFromDrawer
                ? getFileImportReverseFromDrawerRoute(reverseFileImportId)
                : getFileImportReverseRoute(reverseFileImportId)
            }
            dismissRoute={
              reverseFromDrawer
                ? `/${PAYMENT_ACTIVITY_ROUTE}/file-import/${reverseFileImportId}`
                : `/${PAYMENT_ACTIVITY_ROUTE}/file-import`
            }
            onComplete={() => {
              navigate(`/${PAYMENT_ACTIVITY_ROUTE}/file-import`);
            }}
          />
        </SudoProvider>
      )}
    </LoadingSkeleton>
  );
};

export default FileImportContainer;
